/**
 * 🔒 PII TOKEN MANAGER
 * Manages short-lived PII access tokens in memory (not localStorage for security)
 * Tokens expire after 5 minutes and must be renewed
 */

import { PIIAccessToken } from '@/components/security/PasswordReAuthDialog';

class PIITokenManager {
  private token: string | null = null;
  private expiresAt: Date | null = null;
  private timeoutId: NodeJS.Timeout | null = null;

  /**
   * Store PII access token in memory
   */
  setToken(piiToken: PIIAccessToken): void {
    this.token = piiToken.token;
    this.expiresAt = new Date(piiToken.expiresAt);

    // Clear any existing timeout
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // Auto-clear token when it expires
    const expiresIn = piiToken.expiresIn * 1000; // Convert to milliseconds
    this.timeoutId = setTimeout(() => {
      this.clearToken();
    }, expiresIn);

  }

  /**
   * Get current PII access token if valid
   */
  getToken(): string | null {
    if (!this.token || !this.expiresAt) {
      return null;
    }

    // Check if token has expired
    if (new Date() >= this.expiresAt) {
      this.clearToken();
      return null;
    }

    return this.token;
  }

  /**
   * Check if we have a valid token
   */
  hasValidToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Get remaining time in seconds
   */
  getRemainingTime(): number {
    if (!this.expiresAt) return 0;

    const now = new Date();
    const remaining = Math.max(0, Math.floor((this.expiresAt.getTime() - now.getTime()) / 1000));
    return remaining;
  }

  /**
   * Clear token from memory
   */
  clearToken(): void {
    this.token = null;
    this.expiresAt = null;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

  }

  /**
   * Check if token will expire soon (within 30 seconds)
   */
  willExpireSoon(): boolean {
    return this.getRemainingTime() < 30;
  }
}

// Export singleton instance
export const piiTokenManager = new PIITokenManager();

export default piiTokenManager;
